import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationApi,
} from '@/services/api/notifications'

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

const PAGE_SIZE = 20

const Notifications = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationApi[]>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterUnread, setFilterUnread] = useState(false)

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1

  const fetchPage = useCallback(async (p: number, unreadOnly: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const res = await getNotifications({
        page: p,
        limit: PAGE_SIZE,
        unreadOnly,
      })
      setNotifications(res.notifications)
      setTotal(res.meta.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications')
      setNotifications([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPage(page, filterUnread)
  }, [page, filterUnread, fetchPage])

  const handleItemClick = async (n: NotificationApi) => {
    if (!n.read) {
      try {
        await markNotificationRead(n.id)
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))
        )
      } catch {
        // ignore
      }
    }
    if (n.link) {
      navigate(n.link)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead()
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })))
      fetchPage(page, filterUnread)
    } catch {
      // ignore
    }
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Lexend Deca' }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1
          className="text-2xl font-semibold"
          style={{ color: '#212126', fontWeight: 600, fontSize: '24px' }}
        >
          Notifications
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              setFilterUnread(false)
              setPage(1)
            }}
            className="px-4 py-2 rounded-md transition-colors"
            style={{
              fontWeight: !filterUnread ? 600 : 400,
              fontSize: '14px',
              color: !filterUnread ? '#FFFFFF' : '#69697A',
              backgroundColor: !filterUnread ? '#212126' : '#F2F4F2',
            }}
          >
            All
          </button>
          <button
            onClick={() => {
              setFilterUnread(true)
              setPage(1)
            }}
            className="px-4 py-2 rounded-md transition-colors"
            style={{
              fontWeight: filterUnread ? 600 : 400,
              fontSize: '14px',
              color: filterUnread ? '#FFFFFF' : '#69697A',
              backgroundColor: filterUnread ? '#212126' : '#F2F4F2',
            }}
          >
            Unread
          </button>
          {notifications.some((n) => !n.read) && (
            <button
              onClick={handleMarkAllRead}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-sm"
              style={{ color: '#212126' }}
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="py-12 text-center" style={{ color: '#69697A' }}>
          Loading...
        </div>
      )}

      {error && (
        <div className="py-12 text-center" style={{ color: '#E93323' }}>
          {error}
        </div>
      )}

      {!loading && !error && notifications.length === 0 && (
        <div
          className="py-12 text-center rounded-lg border border-gray-200"
          style={{ color: '#69697A', backgroundColor: '#F9FAFB' }}
        >
          No notifications
        </div>
      )}

      {!loading && !error && notifications.length > 0 && (
        <>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleItemClick(n)}
                className="flex items-start justify-between gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <h3
                    className="font-semibold mb-1"
                    style={{
                      fontWeight: 600,
                      color: '#212126',
                      fontSize: '14px',
                    }}
                  >
                    {n.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{
                      color: '#69697A',
                      fontSize: '12px',
                      lineHeight: '20px',
                    }}
                  >
                    {n.message}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!n.read && (
                    <div
                      className="rounded-full"
                      style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#13AB5B',
                      }}
                    />
                  )}
                  <span
                    className="text-xs whitespace-nowrap"
                    style={{ color: '#69697A', fontSize: '12px' }}
                  >
                    {formatRelativeTime(n.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontSize: '14px', color: '#212126' }}
              >
                Previous
              </button>
              <span className="text-sm" style={{ color: '#69697A' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontSize: '14px', color: '#212126' }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Notifications
